import isNumber from "lodash.isnumber";
import Polyglot, { InterpolationOptions } from "node-polyglot";
import { IRapportConfig } from "./config";
import { LocaleCode } from "./locale";
import { IPhraseOptions, Phrase } from "./phrase";

export type TranslateFuncType = (
  strs: TemplateStringsArray,
  ...values: any[]
) => string;

export interface II18n {
  /**
   * Translates a phrase with the passed options to the currently set locale
   *
   * @param {string} phrase
   * @param {IPhraseOptions} [options={}]
   * @returns {string} The phrase in the current set locale
   * @memberof II18n
   */
  _translate(phrase: string, options?: IPhraseOptions): string;

  /**
   * Template literal tag function
   * Translates a phrase to the currently set locale
   * More information about template literal tag functions:
   * https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Template_literals#Tagged_templates
   *
   * @param {TemplateStringsArray} strs
   * @param {...any[]} values
   * @returns {string} The phrase in the current set locale
   * @memberof II18n
   */
  _t(strs: TemplateStringsArray, ...values: any[]): string;

  /**
   * Convenience function for translating abbreviations
   *
   * @param {string} the abbreviation to translate
   * @param {string} the full word that the abbreviation represents
   * @returns {string} The abbreviation in the current set locale
   * @memberof II18n
   */
  _abbreviation(phrase: string, full: string): string;

  /**
   * Convenience function for translating abbreviations
   *
   * @param {string} the abbreviation to translate
   * @param {string} the full word that the abbreviation represents
   * @returns {string} The abbreviation in the current set locale
   * @memberof II18n
   */
  _abbr(phrase: string, full: string): string;

  /**
   * Sets locale to use for translations.
   * Loads phrases from file. Logs a warning if file not found
   *
   * @param {LocaleCode} localeCode
   * @param {boolean} [forceReload]
   * @returns {Promise<boolean>} Returns true if a new locale is set.  False otherwise.
   * @memberof II18n
   */
  setLocale(localeCode: LocaleCode, forceReload?: boolean): Promise<boolean>;

  /**
   * Sets locale to use for translations.
   * Loads phrases from file synchronously. Logs a warning if file not found
   *
   * @param {LocaleCode} localeCode
   * @param {boolean} [forceReload]
   * @returns {Promise<boolean>} Returns true if a new locale is set.  False otherwise.
   * @memberof II18n
   */
  setLocaleSync(localeCode: LocaleCode, forceReload?: boolean): boolean;
}

export default abstract class I18nBase implements II18n {
  private static _genTokenRegex(prefix: string, suffix: string): RegExp {
    const escapeRegex = /[.*+?^${}()|[\]\\]/g;
    const escapedPrefix = prefix.replace(escapeRegex, "\\$&");
    const escapedSuffix = suffix.replace(escapeRegex, "\\$&");
    return new RegExp(escapedPrefix + "(.*?)" + escapedSuffix, "gm");
  }

  protected readonly TOKEN_REGEX: RegExp;
  protected readonly NATIVE_LOCALE_CODE: LocaleCode = "en";

  constructor(
    public readonly config: IRapportConfig,
    protected polyglot: Polyglot
  ) {
    const { prefix, suffix } = this.config.interpolation;
    this.TOKEN_REGEX = I18nBase._genTokenRegex(prefix, suffix);
  }

  public abstract setLocaleSync(
    localeCode: LocaleCode,
    forceReload?: boolean
  ): boolean;

  public async setLocale(
    localeCode: LocaleCode,
    forceReload?: boolean
  ): Promise<boolean> {
    return this.setLocaleSync(localeCode, forceReload);
  }

  public _translate(phrase: string, options: IPhraseOptions = {}): string {
    return this._polyglotTranslate(phrase, options);
  }

  public _t(strings: TemplateStringsArray, ...values: any[]): string {
    const phrase = this._constructPhrase(strings, values);
    const options = this._constructOptions(values);
    return this._polyglotTranslate(phrase, options);
  }

  public _abbreviation(phrase: string, full: string): string {
    const options: IPhraseOptions = { context: "Abbreviation for " + full };
    return this._polyglotTranslate(phrase, options);
  }

  public _abbr(phrase: string, full: string): string {
    return this._abbreviation(phrase, full);
  }

  protected _polyglotTranslate(
    phrase: string,
    options: IPhraseOptions
  ): string {
    const polyglotOptions = this._genPolyglotOptions(options);
    const { phraseKey, indexedOptions } = this._encodePolyglotParameters(
      phrase,
      polyglotOptions
    );
    const translation = this.polyglot.t(phraseKey, indexedOptions);
    const foundTranslation = translation && phraseKey !== translation;

    if (!foundTranslation) {
      console.warn(
        `Warning: Missing translation for phrase "${phrase}" in language: ${this.polyglot.locale()}`
      );
      const fallbackPhrase = new Phrase({
        msgId: phrase,
        msgStr: options.plural ? [phrase, options.plural] : [phrase]
      });
      this.polyglot.extend(fallbackPhrase.toPolyglotPhraseObj());
      return this.polyglot.t(phraseKey, indexedOptions);
    }

    return translation;
  }

  protected _constructPhrase(
    strings: TemplateStringsArray,
    values: any[]
  ): string {
    let phrase = "";

    strings.forEach((str, index) => {
      phrase += str;
      if (index >= values.length) {
        return;
      }
      phrase += `%{_${index}_}`;
    });

    return phrase;
  }

  protected _constructOptions(
    values: Array<string | number>
  ): InterpolationOptions {
    const options: InterpolationOptions = {};
    values.forEach((value, index) => {
      options[`_${index}_`] = value;
    });
    return options;
  }

  protected _genPolyglotOptions(options: IPhraseOptions): InterpolationOptions {
    // tslint:disable-next-line:variable-name
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { count: smart_count, default: _ } = options;
    const polyglotOptions: InterpolationOptions = {};

    if (isNumber(smart_count)) {
      polyglotOptions.smart_count = smart_count;
    }

    if (_) {
      polyglotOptions._ = _;
    }

    return {
      ...polyglotOptions,
      ...options
    };
  }

  private _encodePolyglotParameters(
    phrase: string,
    options: IPhraseOptions
  ): { phraseKey: string; indexedOptions: Polyglot.InterpolationOptions } {
    const tokens = Phrase.getTokensFromPhrase(
      phrase,
      options.plural,
      this.TOKEN_REGEX
    );
    tokens.forEach((token, index) => {
      options[`_${index}_`] = options[token];
    });

    const phraseKey = Phrase.genPhraseKey(
      phrase,
      options.plural,
      this.TOKEN_REGEX
    );

    return {
      phraseKey,
      indexedOptions: options
    };
  }
}
